/* ============================================================================
   Segmented control.

   El indicador se mueve con DOS resortes independientes (x y ancho): un solo
   resorte sobre una distancia 2D se desincroniza cuando los ejes llevan
   velocidades distintas.

   · Respuesta al pointer-down, no al click: la retroalimentación es inmediata.
   · El indicador escribe transform/width por ref, sin re-render por frame.
   · Patrón radiogroup: ←/→ cambian selección; el foco visible es de teclado.
   ========================================================================== */

import { useEffect, useLayoutEffect, useRef } from 'react'
import { SPRING, SpringValue } from '../lib/spring'
import { useFilterLabelId } from './ui'
import './segmented.css'

export interface SegmentedOption<T extends string> {
  value: T
  label: string
  /** Etiqueta larga para lectores de pantalla. */
  hint?: string
}

export interface SegmentedProps<T extends string> {
  options: readonly SegmentedOption<T>[]
  value: T
  onChange: (value: T) => void
  /** Etiqueta accesible del grupo. Sólo se usa si no hay rótulo visible. */
  ariaLabel: string
  /** Id del rótulo visible. Si no se pasa, se toma del FilterField que envuelve. */
  ariaLabelledBy?: string
  size?: 'sm' | 'md'
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  ariaLabelledBy,
  size = 'sm',
}: SegmentedProps<T>) {
  /* Si el control vive dentro de un FilterField, el nombre accesible es el
     rótulo VISIBLE, no un aria-label paralelo: así el nombre contiene el texto
     que se ve, que es lo que exige «Label in Name» (WCAG 2.5.3). */
  const contextLabelId = useFilterLabelId()
  const labelledBy = ariaLabelledBy ?? contextLabelId
  const listRef = useRef<HTMLDivElement | null>(null)
  const pillRef = useRef<HTMLSpanElement | null>(null)
  const btnRefs = useRef<Map<string, HTMLButtonElement>>(new Map())
  const xSpring = useRef<SpringValue | null>(null)
  const wSpring = useRef<SpringValue | null>(null)
  const primed = useRef(false)

  if (xSpring.current === null) xSpring.current = new SpringValue(0, SPRING.move)
  if (wSpring.current === null) wSpring.current = new SpringValue(0, SPRING.move)

  useEffect(() => {
    const sx = xSpring.current!
    const sw = wSpring.current!
    const write = () => {
      const pill = pillRef.current
      if (!pill) return
      pill.style.transform = `translate3d(${sx.value}px, 0, 0)`
      pill.style.width = `${sw.value}px`
    }
    const offX = sx.onChange(write)
    const offW = sw.onChange(write)
    write()
    return () => {
      offX()
      offW()
    }
  }, [])

  useLayoutEffect(() => {
    const list = listRef.current
    const btn = btnRefs.current.get(value)
    if (!list || !btn) return
    const target = btn.offsetLeft
    const width = btn.offsetWidth
    if (!primed.current) {
      // Primer render: colocar sin animar. Nada se mueve solo al cargar.
      xSpring.current!.set(target)
      wSpring.current!.set(width)
      primed.current = true
      return
    }
    xSpring.current!.to(target)
    wSpring.current!.to(width)
  }, [value, options])

  // Reposicionar si el contenedor cambia de tamaño.
  useEffect(() => {
    const list = listRef.current
    if (!list || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => {
      const btn = btnRefs.current.get(value)
      if (!btn) return
      xSpring.current!.set(btn.offsetLeft)
      wSpring.current!.set(btn.offsetWidth)
    })
    ro.observe(list)
    return () => ro.disconnect()
  }, [value])

  const move = (delta: number) => {
    const index = options.findIndex((o) => o.value === value)
    const next = options[(index + delta + options.length) % options.length]
    if (next) {
      onChange(next.value)
      btnRefs.current.get(next.value)?.focus()
    }
  }

  return (
    <div
      className={`segmented segmented--${size}`}
      role="radiogroup"
      {...(labelledBy ? { 'aria-labelledby': labelledBy } : { 'aria-label': ariaLabel })}
      ref={listRef}
    >
      <span className="segmented__pill" ref={pillRef} aria-hidden="true" />
      {options.map((option) => {
        const selected = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            className={`segmented__option${selected ? ' is-selected' : ''}`}
            ref={(node) => {
              if (node) btnRefs.current.set(option.value, node)
              else btnRefs.current.delete(option.value)
            }}
            onPointerDown={(event) => {
              // Compromiso en pointer-down: esperar al click se siente muerto.
              if (event.pointerType === 'mouse' && event.button !== 0) return
              if (!selected) onChange(option.value)
            }}
            onClick={() => {
              if (!selected) onChange(option.value)
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                event.preventDefault()
                move(1)
              } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                event.preventDefault()
                move(-1)
              }
            }}
          >
            <span>{option.label}</span>
            {option.hint ? <span className="sr-only">{option.hint}</span> : null}
          </button>
        )
      })}
    </div>
  )
}
